import React, { useState, useEffect, useCallback } from 'react';
import { 
  Card, Table, Tag, Button, Space, Typography, Tooltip, Badge, 
  Modal, Spin, Alert, message, Form, Input, Select
} from 'antd';
import { 
  SyncOutlined, CheckCircleOutlined, WarningOutlined, 
  CloseCircleOutlined, ClockCircleOutlined, SearchOutlined,
  GlobalOutlined, EditOutlined
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { fetchWorkers, checkWorkerHealth, checkAllWorkersHealth, updateWorker } from '../actions';

const { Title, Text } = Typography;
const { Option } = Select;

const WorkerManagement = () => {
  const dispatch = useDispatch();
  const { workers, loading } = useSelector(state => state.adminSettings);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [detailVisible, setDetailVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editForm] = Form.useForm();

  // Memoize functions to prevent recreation on every render
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      // First fetch the latest worker list
      await dispatch(fetchWorkers());
      
      // Then check health of all workers
      const result = await dispatch(checkAllWorkersHealth());
      
      if (result.success) {
        message.success('Cập nhật trạng thái của tất cả worker thành công');
      } else {
        message.error('Có lỗi xảy ra khi cập nhật trạng thái worker');
      }
    } catch (error) {
      console.error('Error refreshing workers:', error);
      message.error('Không thể cập nhật danh sách worker');
    } finally {
      setRefreshing(false);
    }
  }, [dispatch]);

  const handleCheckHealth = useCallback(async (workerId) => {
    const worker = workers.find(w => w.id === workerId);
    setSelectedWorker(worker);
    setDetailVisible(true);
    
    try {
      // Check worker health and show result message
      const healthResult = await dispatch(checkWorkerHealth(workerId));
      
      if (healthResult.success) {
        message.success(`Kiểm tra worker "${worker.name}" thành công`);
      } else {
        message.warning(`Worker "${worker.name}" đang gặp vấn đề kết nối`);
      }
    } catch (error) {
      console.error(`Error checking health for worker ${workerId}:`, error);
      message.error(`Không thể kiểm tra trạng thái của worker "${worker.name}"`);
    }
  }, [dispatch, workers]);

  const handleEditWorker = useCallback((worker) => {
    setSelectedWorker(worker);
    editForm.setFieldsValue({
      name: worker.name,
      url: worker.url,
      type: worker.type,
      description: worker.description,
    });
    setEditModalVisible(true);
  }, [editForm]);

  const handleUpdateWorker = useCallback(async (values) => {
    try {
      // Dispatch update worker action
      const result = await dispatch(updateWorker({ ...selectedWorker, ...values }));
      
      if (result.success) {
        message.success('Cập nhật worker thành công');
        setEditModalVisible(false);
        dispatch(fetchWorkers()); // Refresh worker list
      } else {
        message.error('Có lỗi xảy ra khi cập nhật worker');
      }
    } catch (error) {
      console.error('Error updating worker:', error);
      message.error('Không thể cập nhật worker');
    }
  }, [dispatch, selectedWorker]);

  // Only run on mount - empty dependency array
  useEffect(() => {
    dispatch(fetchWorkers());
  }, [dispatch]);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'online':
        return <Badge status="success" text="Hoạt động" />;
      case 'offline':
        return <Badge status="default" text="Ngoại tuyến" />;
      case 'error':
        return <Badge status="error" text="Lỗi" />;
      case 'warning':
        return <Badge status="warning" text="Cảnh báo" />;
      default:
        return <Badge status="processing" text="Không xác định" />;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'online':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'offline':
        return <CloseCircleOutlined style={{ color: '#d9d9d9' }} />;
      case 'error':
        return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
      case 'warning':
        return <WarningOutlined style={{ color: '#faad14' }} />;
      default:
        return <ClockCircleOutlined style={{ color: '#1890ff' }} />;
    }
  };

  const getTypeTag = (type) => {
    switch (type) {
      case 'sync':
        return <Tag color="green">Đồng bộ dữ liệu</Tag>;
      case 'queue':
        return <Tag color="purple">Xử lý task</Tag>;
      default:
        return <Tag>Không xác định</Tag>;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Chưa kiểm tra';
    
    const date = new Date(dateString);
    return date.toLocaleString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const columns = [
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 170,
      render: status => getStatusBadge(status),
      sorter: (a, b) => {
        const statusOrder = { online: 0, warning: 1, error: 2, offline: 3 };
        return statusOrder[a.status] - statusOrder[b.status];
      }
    },
    {
      title: 'Tên Worker',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <Space>
          {getStatusIcon(record.status)}
          <Text strong>{name}</Text>
        </Space>
      ),
    },
    {
      title: 'Loại',
      dataIndex: 'type',
      key: 'type',
      width: 150,
      render: type => getTypeTag(type),
      filters: [
        { text: 'Đồng bộ dữ liệu', value: 'sync' },
        { text: 'Xử lý task', value: 'queue' },
      ],
      onFilter: (value, record) => record.type === value,
    },
    {
      title: 'URL',
      dataIndex: 'url',
      key: 'url',
      ellipsis: true,
      render: url => (
        <Tooltip title={url}>
          <a href={url} target="_blank" rel="noopener noreferrer">
            <Space>
              <GlobalOutlined />
              {url}
            </Space>
          </a>
        </Tooltip>
      ),
    },
    {
      title: 'Kiểm tra gần nhất',
      dataIndex: 'lastHealthyCheck',
      key: 'lastHealthyCheck',
      width: 200,
      render: date => formatDate(date),
      sorter: (a, b) => {
        const dateA = a.lastHealthyCheck ? new Date(a.lastHealthyCheck).getTime() : 0;
        const dateB = b.lastHealthyCheck ? new Date(b.lastHealthyCheck).getTime() : 0;
        return dateA - dateB;
      }
    },
    {
      title: 'Thao tác',
      key: 'action',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Button 
            type="primary" 
            icon={<SearchOutlined />}
            onClick={() => handleCheckHealth(record.id)}
            size="small"
            loading={loading && selectedWorker?.id === record.id}
          >
            Kiểm tra
          </Button>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEditWorker(record)}
            size="small"
          >
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="worker-management">
      <Card 
        title={<Title level={5}>Quản lý Worker</Title>} 
        extra={
          <Space>
            <Button
              onClick={handleRefresh}
              loading={refreshing || loading}
              icon={<SyncOutlined />}
            >
              Cập nhật trạng thái
            </Button>
            <Button 
              type="primary"
              onClick={() => dispatch(fetchWorkers())}
              loading={loading && !refreshing}
            >
              Làm mới
            </Button>
          </Space>
        }
        bordered={false}
        className="worker-card"
      >
        <Table 
          dataSource={workers ? workers.map(worker => ({ ...worker, key: worker.id })) : []}
          columns={columns}
          loading={loading && !detailVisible}
          pagination={{ pageSize: 10 }}
          rowClassName={(record) => {
            if (record.status === 'offline') return 'worker-row-offline';
            if (record.status === 'error') return 'worker-row-error';
            if (record.status === 'warning') return 'worker-row-warning';
            return '';
          }}
        />
        
        <Modal
          title={`Chi tiết Worker: ${selectedWorker?.name || ''}`}
          open={detailVisible}
          onCancel={() => setDetailVisible(false)}
          footer={[
            <Button 
              key="close" 
              onClick={() => setDetailVisible(false)}
            >
              Đóng
            </Button>,
            <Button 
              key="check" 
              type="primary" 
              icon={<SyncOutlined />}
              onClick={() => selectedWorker && handleCheckHealth(selectedWorker.id)}
              loading={loading && selectedWorker}
            >
              Kiểm tra lại
            </Button>,
          ]}
          width={700}
        >
          {selectedWorker ? (
            <Spin spinning={loading && selectedWorker}>
              <div className="worker-detail">
                <div className="worker-health-status">
                  {selectedWorker.status === 'online' && (
                    <Alert
                      message="Hoạt động bình thường"
                      description="Worker đang hoạt động và có thể kết nối được."
                      type="success"
                      showIcon
                    />
                  )}
                  
                  {selectedWorker.status === 'warning' && (
                    <Alert
                      message="Cảnh báo"
                      description="Worker đang hoạt động nhưng có một số vấn đề."
                      type="warning"
                      showIcon
                    />
                  )}
                  
                  {selectedWorker.status === 'error' && (
                    <Alert
                      message="Lỗi"
                      description="Worker đang gặp lỗi và không thể hoạt động bình thường."
                      type="error"
                      showIcon
                    />
                  )}
                  
                  {selectedWorker.status === 'offline' && (
                    <Alert
                      message="Ngoại tuyến"
                      description="Không thể kết nối tới worker này."
                      type="info"
                      showIcon
                    />
                  )}
                </div>
                
                <div className="worker-info" style={{ marginTop: 16 }}>
                  <table className="detail-table">
                    <tbody>
                      <tr>
                        <td><strong>ID:</strong></td>
                        <td>{selectedWorker.id}</td>
                      </tr>
                      <tr>
                        <td><strong>Tên:</strong></td>
                        <td>{selectedWorker.name}</td>
                      </tr>
                      <tr>
                        <td><strong>URL:</strong></td>
                        <td>
                          <a href={selectedWorker.url} target="_blank" rel="noopener noreferrer">
                            {selectedWorker.url}
                          </a>
                        </td>
                      </tr>
                      <tr>
                        <td><strong>Loại:</strong></td>
                        <td>{getTypeTag(selectedWorker.type)}</td>
                      </tr>
                      <tr>
                        <td><strong>Trạng thái:</strong></td>
                        <td>{getStatusBadge(selectedWorker.status)}</td>
                      </tr>
                      <tr>
                        <td><strong>Kiểm tra gần nhất:</strong></td>
                        <td>{formatDate(selectedWorker.lastHealthyCheck)}</td>
                      </tr>
                      <tr>
                        <td><strong>Mô tả:</strong></td>
                        <td>{selectedWorker.description || 'Không có mô tả'}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </Spin>
          ) : (
            <div>Đang tải thông tin...</div>
          )}
        </Modal>
        
        <Modal
          title={`Sửa Worker: ${selectedWorker?.name || ''}`}
          open={editModalVisible}
          onCancel={() => setEditModalVisible(false)}
          footer={[
            <Button 
              key="cancel" 
              onClick={() => setEditModalVisible(false)}
            >
              Hủy
            </Button>,
            <Button 
              key="submit" 
              type="primary" 
              onClick={() => editForm.submit()}
              loading={loading}
            >
              Lưu
            </Button>,
          ]}
          width={600}
        >
          {selectedWorker ? (
            <Form
              form={editForm}
              layout="vertical"
              onFinish={handleUpdateWorker}
              initialValues={{
                name: selectedWorker.name,
                url: selectedWorker.url,
                type: selectedWorker.type,
                description: selectedWorker.description,
              }}
            >
              <Form.Item
                name="name"
                label="Tên Worker"
                rules={[{ required: true, message: 'Vui lòng nhập tên worker' }]}
              >
                <Input />
              </Form.Item>
              
              <Form.Item
                name="url"
                label="URL"
                rules={[{ required: true, message: 'Vui lòng nhập URL' }]}
              >
                <Input />
              </Form.Item>
              
              <Form.Item
                name="type"
                label="Loại"
                rules={[{ required: true, message: 'Vui lòng chọn loại worker' }]}
              >
                <Select>
                  <Option value="sync">Đồng bộ dữ liệu</Option>
                  <Option value="queue">Xử lý task</Option>
                </Select>
              </Form.Item>
              
              <Form.Item
                name="description"
                label="Mô tả"
              >
                <Input.TextArea rows={4} />
              </Form.Item>
            </Form>
          ) : (
            <div>Đang tải thông tin worker...</div>
          )}
        </Modal>
      </Card>
    </div>
  );
};

export default WorkerManagement;